<template>
  <Transition name="dialog">
    <div
      v-if="show"
      class="fixed inset-0 bg-black/50 z-50 overflow-y-auto"
      @click.self="handleCancel"
    >
      <div class="flex min-h-full items-center justify-center px-6 py-12">
        <div
          class="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl animate-in fade-in zoom-in-95 duration-200"
          @click.stop
        >
          <!-- 標題 -->
          <div class="text-center mb-4">
            <div class="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <span class="text-3xl">🔔</span>
            </div>
            <h3 class="text-lg font-bold text-textMain">開啟通知提醒</h3>
          </div>

          <!-- 說明內容 - iOS -->
          <div v-if="isIOS" class="space-y-4 mb-6">
            <p class="text-sm text-textMuted leading-relaxed">
              接下來系統會詢問您是否允許通知權限
            </p>
            <p class="text-sm text-textMuted leading-relaxed">
              請點擊<span class="font-bold text-textMain">「允許」</span>。
            </p>

            <div class="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p class="text-xs font-bold text-blue-900 mb-2">💡 建議設定（取得最佳體驗）</p>
              <p class="text-xs text-blue-800 leading-relaxed mb-2">
                允許權限後，建議前往：
              </p>
              <div class="bg-white rounded-lg p-3 text-xs text-blue-900 font-mono">
                設定 → App → 寶寶接種小幫手 → 通知 → 橫幅樣式
              </div>
              <p class="text-xs text-blue-800 leading-relaxed mt-2">
                將橫幅樣式設定為<span class="font-bold">「持續」</span>，這樣才不會錯過重要的疫苗提醒。
              </p>
            </div>

            <div class="flex items-start gap-2 text-xs text-textMuted">
              <span>ℹ️</span>
              <p class="leading-relaxed">
                您可以隨時在「設定」頁面重新調整通知選項。
              </p>
            </div>
          </div>

          <!-- 說明內容 - Android -->
          <div v-else class="space-y-4 mb-6">
            <p class="text-sm text-textMuted leading-relaxed">
              接下來系統會詢問您是否允許通知權限
            </p>
            <p class="text-sm text-textMuted leading-relaxed">
              請點擊<span class="font-bold text-textMain">「允許」</span>以接收疫苗提醒通知。
            </p>

            <div class="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p class="text-xs font-bold text-blue-900 mb-2">💡 小提醒</p>
              <p class="text-xs text-blue-800 leading-relaxed">
                如果您不小心點擊了「拒絕」，可以到：
              </p>
              <div class="bg-white rounded-lg p-3 text-xs text-blue-900 font-mono mt-2">
                設定 → 應用程式 → 寶寶接種小幫手 → 通知
              </div>
              <p class="text-xs text-blue-800 leading-relaxed mt-2">
                手動開啟通知權限。
              </p>
            </div>

            <div class="flex items-start gap-2 text-xs text-textMuted">
              <span>ℹ️</span>
              <p class="leading-relaxed">
                您可以隨時在「設定」頁面重新調整通知選項。
              </p>
            </div>
          </div>

          <!-- 按鈕 -->
          <div class="flex gap-3">
            <button
              @click="handleCancel"
              class="flex-1 py-3 rounded-xl font-bold text-textMuted bg-gray-100 active:scale-95 transition-all"
            >
              取消
            </button>
            <button
              @click="handleConfirm"
              class="flex-1 py-3 rounded-xl font-bold text-white bg-primary active:scale-95 transition-all"
            >
              我知道了
            </button>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Capacitor } from '@capacitor/core'

interface Props {
  show: boolean
}

defineProps<Props>()

// 判斷是否為 iOS 平台
const isIOS = computed(() => Capacitor.getPlatform() === 'ios')

const emit = defineEmits<{
  confirm: []
  cancel: []
}>()

const handleConfirm = () => {
  emit('confirm')
}

const handleCancel = () => {
  emit('cancel')
}
</script>

<style scoped>
.dialog-enter-active,
.dialog-leave-active {
  transition: opacity 0.2s ease;
}

.dialog-enter-from,
.dialog-leave-to {
  opacity: 0;
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes zoom-in-95 {
  from {
    transform: scale(0.95);
  }
  to {
    transform: scale(1);
  }
}

.animate-in {
  animation: fade-in 0.2s ease, zoom-in-95 0.2s ease;
}
</style>
